import { useState, useRef, useCallback, useEffect } from 'react';
import { getProject, saveProject } from '../firebase/projects';
import { saveBoreholeToSource, fetchBoreholeWithLayers, fetchProjectLayerColors } from '../firebase/boreholeBrowser';
import { serializeProject, deserializeProject } from '../utils/projectSerializer';

/**
 * Hook do ladowania/zapisu pojedynczego projektu z kolekcji przekrojProjects.
 * Obsluguje auto-save z debounce (do projektu) + dwukierunkowy sync z bazą otworów (source DB).
 */
export function useFirestoreProject(projectId) {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [loadingProject, setLoadingProject] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Stan dwukierunkowego sync z bazą otworów
  const [isSavingToSource, setIsSavingToSource] = useState(false);
  const [lastSavedToSource, setLastSavedToSource] = useState(null);
  const [dirtyBoreholeIds, setDirtyBoreholeIds] = useState(() => new Set());

  const saveTimeoutRef = useRef(null);
  const lastSavedDataRef = useRef(null);
  // Fingerprint każdego otworu w wersji "ostatnio zapisanej do bazy otworów"
  // mapa: boreholeId -> fingerprint string
  const sourceFingerprintsRef = useRef(new Map());

  // Fingerprint otworu — tylko pola które trafiają do bazy (saveBoreholeToSource)
  const boreholeFingerprint = (bh) => JSON.stringify({
    name: bh.name || '',
    depth: bh.depth || 0,
    layers: (bh.layers || []).map(l => ({
      name: l.name || '',
      thickness: Number(l.thickness) || 0,
      from: Number(l.from) || 0,
      to: Number(l.to) || 0,
      geotechnicalLayer: l.geotechnicalLayer || '',
      soilState: l.soilState || '',
      color: l.color || '',
      materialType: l.materialType || 'grunt',
      lithologyRockSoil01: l.lithologyRockSoil01 || '',
      lithologyRockSoilFullName01: l.lithologyRockSoilFullName01 || '',
      layeringRockSoil01: l.layeringRockSoil01 || '',
      lithologyRockSoil02: l.lithologyRockSoil02 || '',
      lithologyRockSoilFullName02: l.lithologyRockSoilFullName02 || '',
      layeringRockSoil02: l.layeringRockSoil02 || '',
      lithologyRockSoil03: l.lithologyRockSoil03 || '',
      lithologyRockSoilFullName03: l.lithologyRockSoilFullName03 || '',
      genesisAndStratigraphy: l.genesisAndStratigraphy || '',
    })),
    waterLevels: (bh.waterLevels || []).map(w => ({
      type: w.type,
      sourceType: w.sourceType,
      depth: w.depth,
      depthFrom: w.depthFrom,
      depthTo: w.depthTo,
    })),
  });

  const loadProject = useCallback(async () => {
    if (!projectId) {
      setLoadingProject(false);
      return null;
    }

    setLoadingProject(true);
    setLoadError(null);
    try {
      const projectDoc = await getProject(projectId);
      if (!projectDoc) {
        setLoadError('Projekt nie istnieje lub brak dostepu.');
        setLoadingProject(false);
        return null;
      }

      const data = projectDoc.data || {};
      const deserialized = Object.keys(data).length > 0
        ? deserializeProject(data)
        : null;

      // Wyczyść stare fingerprinty — nowy projekt = nowy baseline. Pierwsze wywołanie
      // markDirtyBoreholes po załadowaniu zainicjalizuje fingerprinty z aktualnego stanu
      // GeologicalProfile (omija różnice raw-deserialize vs post-useState).
      sourceFingerprintsRef.current = new Map();
      setDirtyBoreholeIds(new Set());

      setLoadingProject(false);
      return deserialized;
    } catch (err) {
      console.error('Blad ladowania projektu:', err);
      setLoadError('Blad ladowania projektu.');
      setLoadingProject(false);
      return null;
    }
  }, [projectId]);

  const persistProject = useCallback(async (projectState) => {
    if (!projectId) return;

    setIsSaving(true);
    try {
      const serialized = serializeProject(projectState);
      await saveProject(projectId, serialized);
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
      lastSavedDataRef.current = JSON.stringify(serialized);
    } catch (err) {
      console.error('Blad zapisu projektu:', err);
    } finally {
      setIsSaving(false);
    }
  }, [projectId]);

  const debouncedSave = useCallback((projectState) => {
    setHasUnsavedChanges(true);

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      const serialized = JSON.stringify(serializeProject(projectState));
      if (serialized !== lastSavedDataRef.current) {
        persistProject(projectState);
      } else {
        setHasUnsavedChanges(false);
      }
    }, 3000);
  }, [persistProject]);

  // Śledzi brudne otwory (zmienione vs ostatni snapshot bazy) — BEZ auto-zapisu.
  // Zapis ręczny przez `saveDirtyBoreholesToSource`.
  //
  // Pierwsze wywołanie po załadowaniu projektu (gdy fingerprint danego otworu jest jeszcze
  // pusty) INICJALIZUJE baseline z aktualnego stanu — nie oznacza tego otworu jako brudny.
  // Dzięki temu omijamy drobne różnice serializacji raw-Firestore vs po-useState.
  const markDirtyBoreholes = useCallback((boreholes) => {
    if (!Array.isArray(boreholes)) return;
    const newDirty = new Set();
    const skipReasons = [];
    for (const bh of boreholes) {
      if (!bh.sourceBoreholeId) { skipReasons.push(`${bh.name || bh.id}: no sourceBoreholeId`); continue; }
      const fp = boreholeFingerprint(bh);
      const lastFp = sourceFingerprintsRef.current.get(bh.id);
      if (lastFp === undefined) {
        sourceFingerprintsRef.current.set(bh.id, fp);
        continue;
      }
      if (fp !== lastFp) newDirty.add(bh.id);
    }
    if (skipReasons.length > 0) console.log('[markDirty] pominiete:', skipReasons);
    console.log('[markDirty] brudne ID:', Array.from(newDirty), 'nazwy:', boreholes.filter(b => newDirty.has(b.id)).map(b => b.name));
    setDirtyBoreholeIds(newDirty);
  }, []);

  // Manualny zapis brudnych otworów do bazy źródłowej.
  // Zwraca obiekt {saved, errors, errorMessages} — można pokazać użytkownikowi.
  const saveDirtyBoreholesToSource = useCallback(async (boreholes) => {
    if (!Array.isArray(boreholes)) return { saved: 0, errors: 0, errorMessages: [] };
    const dirty = boreholes.filter(b => dirtyBoreholeIds.has(b.id) && b.sourceBoreholeId);
    if (dirty.length === 0) return { saved: 0, errors: 0, errorMessages: [] };

    setIsSavingToSource(true);
    const result = { saved: 0, errors: 0, errorMessages: [] };
    try {
      const settled = await Promise.allSettled(dirty.map(b => saveBoreholeToSource(b)));
      settled.forEach((s, i) => {
        if (s.status === 'fulfilled' && s.value?.ok) {
          result.saved++;
          // Po sukcesie zaktualizuj snapshot — od teraz ten otwór jest "czysty"
          sourceFingerprintsRef.current.set(dirty[i].id, boreholeFingerprint(dirty[i]));
        } else {
          result.errors++;
          const reason = s.status === 'rejected' ? s.reason?.message : (s.value?.reason || 'unknown');
          result.errorMessages.push(`${dirty[i].name || dirty[i].id}: ${reason}`);
        }
      });
      // Odśwież dirty po zapisie
      const remainingDirty = new Set();
      for (const bh of boreholes) {
        if (!bh.sourceBoreholeId) continue;
        const fp = boreholeFingerprint(bh);
        const lastFp = sourceFingerprintsRef.current.get(bh.id);
        if (fp !== lastFp) remainingDirty.add(bh.id);
      }
      setDirtyBoreholeIds(remainingDirty);
      setLastSavedToSource(new Date());
    } catch (err) {
      console.error('Blad zapisu do bazy otworow:', err);
      result.errors++;
      result.errorMessages.push(err.message || String(err));
    } finally {
      setIsSavingToSource(false);
    }
    return result;
  }, [dirtyBoreholeIds]);

  // Przywraca pojedynczy otwór do stanu z bazy otworów (cofnij niezapisane zmiany).
  // Zwraca świeży obiekt borehole pobrany z bazy źródłowej, lub null jeśli nie udało się.
  const revertBoreholeToSource = useCallback(async (borehole) => {
    if (!borehole?.sourceProjectId || !borehole?.sourceBoreholeId) return null;
    try {
      // Słownik kolorów z catNrGeologyLayer — bez tego resolveLayerColor wpada w fallback
      // algorytmiczny (szare/brązowe domyślne) zamiast kolorów ustalonych dla projektu.
      const layerColors = await fetchProjectLayerColors(borehole.sourceProjectId);
      const fresh = await fetchBoreholeWithLayers(borehole.sourceProjectId, borehole.sourceBoreholeId, layerColors);
      if (!fresh) return null;
      // Zachowaj kontekstowe pola (id w edytorze, distance, coordX/Y)
      const merged = {
        ...fresh,
        id: borehole.id,
        distance: borehole.distance,
        coordX: borehole.coordX,
        coordY: borehole.coordY,
      };
      // Zaktualizuj snapshot — od teraz to jest "baseline" dla dirty
      sourceFingerprintsRef.current.set(borehole.id, boreholeFingerprint(merged));
      setDirtyBoreholeIds(prev => {
        const next = new Set(prev);
        next.delete(borehole.id);
        return next;
      });
      return merged;
    } catch (err) {
      console.error('Blad przywracania otworu z bazy:', err);
      return null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  return {
    isSaving,
    lastSaved,
    hasUnsavedChanges,
    loadingProject,
    loadError,
    loadProject,
    saveProject: persistProject,
    debouncedSave,
    // Source DB sync — ręczny
    isSavingToSource,
    lastSavedToSource,
    dirtyBoreholeIds,
    markDirtyBoreholes,
    saveDirtyBoreholesToSource,
    revertBoreholeToSource,
  };
}
